import { useState, useEffect, useCallback, type SyntheticEvent } from 'react'
import { Slide } from '@mui/material'
import { Alert } from '../../primitives/Alert'
import { Box } from '../../primitives/Box'
import { type Toast } from './types'
import { useSnackbarContext } from './use-snackbar-context'
import { getDirection } from './get-direction'

type Props = {
  toast: Toast
}

export const SnackbarToast = ({ toast }: Props) => {
  const [open, setOpen] = useState(toast.visible)
  const { removeToast } = useSnackbarContext()

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (toast.duration !== 0) {
      setTimeout(() => {
        close()
      }, toast.duration || 6000)
    }
  }, [close, toast.duration])

  useEffect(() => {
    if (!toast.visible) {
      close()
    }
  }, [toast.visible, close])

  const handleAnimationExit = () => removeToast(toast.key)

  const handleClose = (_: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    close()
  }

  return (
    <Slide
      in={open}
      timeout={toast.transitionDuration}
      onExited={handleAnimationExit}
      direction={getDirection(toast.position)}
    >
      <Box key={toast.key} onClick={handleClose} tabIndex={0} data-testid="snackbar-message">
        <Alert
          severity={toast.severity}
          variant={toast.variant}
          title={toast.title}
          action={toast.action}
          onClose={toast.onClose}
        >
          {toast.message}
        </Alert>
      </Box>
    </Slide>
  )
}
